import { api } from './client';
import type { Skill } from './skills';

export type AgentStatus = 'active' | 'idle' | 'thinking' | 'acting' | 'error' | string;

export interface AgentSummary {
  id: string;
  name: string;
  type: string;
  status: AgentStatus;
  description?: string;
  skills: string[];
  capabilities?: string[];
  total_runs: number;
  success_rate?: number;
  avg_response_time_ms?: number;
  last_run_at?: string | null;
  is_active: boolean;
}

export interface AgentRun {
  run_id: string;
  skill_id: string;
  executed_at: string;
  status: string;
  message?: string;
  data?: Record<string, unknown>;
}

export interface AgentStats {
  total_runs: number;
  success_runs: number;
  failed_runs: number;
  success_rate: number;
  avg_response_time_ms: number;
  last_run_at?: string | null;
}

export interface AgentDetails {
  id: string;
  name: string;
  type: string;
  status: AgentStatus;
  description: string;
  skills: Skill[];
  capabilities: string[];
  stats: AgentStats;
  recent_runs: AgentRun[];
  last_activity: string;
}

export const agentsService = {
  async list(): Promise<AgentSummary[]> {
    const res = await api.get<{ agents?: AgentSummary[]; count?: number }>('/agent/agents');
    return res.data?.agents ?? [];
  },

  async details(id: string): Promise<AgentDetails | null> {
    if (!id) return null;
    try {
      const res = await api.get<AgentDetails>(`/agent/agents/${encodeURIComponent(id)}`);
      return res.data ?? null;
    } catch {
      return null;
    }
  },

  async runs(id: string, limit = 20): Promise<AgentRun[]> {
    if (!id) return [];
    try {
      const res = await api.get<{ runs?: AgentRun[]; count?: number }>(
        `/agent/agents/${encodeURIComponent(id)}/runs?limit=${limit}`,
      );
      return res.data?.runs ?? [];
    } catch {
      return [];
    }
  },

  async stats(id: string): Promise<AgentStats | null> {
    if (!id) return null;
    try {
      const res = await api.get<{ stats: AgentStats }>(`/agent/agents/${encodeURIComponent(id)}/stats`);
      return res.data?.stats ?? null;
    } catch {
      return null;
    }
  },
};
